import React from "react";
import { Bug, FolderSearch, X } from "lucide-react";
import { formatBotMessage } from "./utils/formatMessage";

// Record searches only. Session context used to ride here as a third tab, but
// it is not a search — no count, never "searched", present from the first turn
// — so it moved to its own header-opened drawer (SessionContextDrawer).
const TABS = [
  { key: "bugs", label: "Bugs", icon: Bug },
  { key: "cases", label: "Cases", icon: FolderSearch },
];

// The section headings the agent is required to use when it presents these
// same results in chat (system_prompt.py CASE_SEARCH_RULE / bug search rule).
const SECTION_HEADING = {
  bugs: "Known Bugs Found",
  cases: "Similar Past Cases Found",
};

const EMPTY_TEXT = {
  bugs: "No known bugs found.",
  cases: "No similar past cases found.",
};

const LOW_RELEVANCE_NOTICE =
  "Search did not yield high relevance matches. Please review the following results.";

/**
 * Normalise the two payload shapes that reach the panel: the panel JSON
 * (match_pct on a 0–100 scale) and the agent's bugs_found/cases_found emit
 * (score on a 0–1 scale).
 */
const normalise = (item, tab) => {
  const raw = Number(item.match_pct ?? item.score) || 0;
  // `description` is the summarizer's paragraph: what the record was AND what
  // it means here, fused. It is the same prose the agent reads out in chat.
  const summarized = !!item.description;
  const body = item.description || item.summary || item.subject || item.title || "";
  const pct = Math.max(0, Math.min(100, Math.round(raw <= 1 ? raw * 100 : raw)));

  // Heading formats are the ones the prompt mandates for the explicit answer:
  //   bugs  — "<issue_key> — Match: <pct>%"
  //   cases — "Case <caseNumber> (<caseId>) — Match: <pct>%"
  let heading;
  if (tab === "bugs") {
    heading = item.bug_id || item.id || item.title || "Unknown";
  } else {
    const number = item.case_number || "Unknown";
    const caseId = item.case_id || "";
    heading = caseId ? `Case ${number} (${caseId})` : `Case ${number}`;
  }

  return {
    key: item.bug_id || item.case_number || item.case_id || item.id || item.title || "",
    heading,
    pct,
    status: item.status || "",
    // The stored resolution blob is a mid-word truncation; once the paragraph
    // covers the outcome it is pure duplication.
    resolution: summarized ? "" : item.resolution,
    body,
    url: item.url,
  };
};

/**
 * ResultsPanel — overlay listing the bugs/cases found by the background
 * searches. Renders them in the same "<id> — Match: n% / Status / Description"
 * layout the agent uses for an explicit in-chat search, so the two never
 * disagree; the only difference is that these arrive silently, on the side.
 */
const ResultsPanel = ({
  open,
  activeTab = "bugs",
  bugs = [],
  cases = [],
  meta = {},
  onTabChange,
  onClose,
}) => {
  if (!open) return null;

  const counts = { bugs: bugs.length, cases: cases.length };
  const items = (activeTab === "bugs" ? bugs : cases).map((item) => normalise(item, activeTab));
  const tabMeta = meta[activeTab] || {};

  return (
    <div className="d5-results-overlay" onClick={onClose}>
      <div
        className="d5-results-panel"
        role="dialog"
        aria-label="Search results"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="d5-results-head">
          <div className="d5-results-tabs">
            {TABS.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                type="button"
                className={`d5-results-tab d5-results-tab-${key} ${activeTab === key ? "d5-results-tab-active" : ""}`}
                onClick={() => onTabChange?.(key)}
              >
                {/* 11px to match the ActionBar chip's icon — the pill cannot
                    be the same height as the chip while carrying a bigger icon. */}
                <Icon size={11} />
                {label}
                {counts[key] !== undefined && (
                  <span className="d5-results-tab-count">{counts[key]}</span>
                )}
              </button>
            ))}
          </div>
          <button type="button" className="d5-results-close" title="Close" onClick={onClose}>
            <X size={15} />
          </button>
        </div>

        <div className={`d5-results-body d5-results-body-${activeTab}`}>
          {/* WHAT THIS PANEL SEARCHED ON. Not the engineer's words: cases and
              bugs are retrieved on a factual summary of the conversation built
              server-side (_resolve_rag_query_or_reuse), and the panel refreshes
              on its own in the background, so without this there is no way to
              tell which question produced the cards on screen -- or that the
              query drifted between two searches that returned different records. */}
          {tabMeta.query && (
            <details className="d5-results-query">
              <summary>Search query</summary>
              <p>{tabMeta.query}</p>
            </details>
          )}
          {/* The server renders this section once and the in-chat answer shows
              that same string, so the two cannot drift. The per-item rendering
              below is the fallback for payloads without it. */}
          {tabMeta.sectionText ? (
            <div
              className="d5-results-section"
              dangerouslySetInnerHTML={{ __html: formatBotMessage(tabMeta.sectionText) }}
            />
          ) : (
          <>
          <h4 className="d5-results-heading">{SECTION_HEADING[activeTab]}</h4>

          {tabMeta.lowRelevance && items.length > 0 && (
            <p className="d5-results-notice">{LOW_RELEVANCE_NOTICE}</p>
          )}

          {items.length === 0 ? (
            <p className="d5-results-empty">
              {tabMeta.searched ? EMPTY_TEXT[activeTab] : `No ${activeTab} searched yet.`}
            </p>
          ) : (
            items.map((item, i) => (
              <div key={item.key || i} className={`d5-results-entry d5-results-entry-${activeTab}`}>
                <p className="d5-results-entry-head">
                  {item.url ? (
                    <a
                      className="d5-results-entry-id"
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={`Open ${item.heading}`}
                    >
                      {item.heading}
                    </a>
                  ) : (
                    <span className="d5-results-entry-id">{item.heading}</span>
                  )}
                  {/* Heading line and nothing else — the explicit answer shows
                      no relevance badge, and the description already says where
                      a record only partly applies. */}
                  <span className="d5-results-entry-match"> — Match: {item.pct}%</span>
                </p>
                {item.status && (
                  <p className="d5-results-entry-line">
                    <span className="d5-results-entry-label">Status:</span> {item.status}
                  </p>
                )}
                {item.body && (
                  <p className="d5-results-entry-line">
                    <span className="d5-results-entry-label">Description:</span>{" "}
                    <span dangerouslySetInnerHTML={{ __html: formatBotMessage(item.body) }} />
                  </p>
                )}
                {item.resolution && (
                  <p className="d5-results-entry-line">
                    <span className="d5-results-entry-label">Resolution:</span> {item.resolution}
                  </p>
                )}
              </div>
            ))
          )}
          </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResultsPanel;
